import React, { useState } from 'react';
import { categories, reports } from '../data';

const IntroText = () => (
  <div className="prose prose-lg max-w-none mb-12">
    <h1 className="text-2xl font-bold mb-4">ConsumerDNA™ Insights Selector</h1>
    <p className="text-gray-600">
      Answer a few quick questions to discover which ConsumerDNA™ insights will have the biggest impact on your specific customer experience challenges.
    </p>
  </div>
);

const CXReportRecommender = () => {
  const [step, setStep] = useState(1);
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const [recommendedInsights, setRecommendedInsights] = useState<typeof reports>([]);
  const [selectedInsights, setSelectedInsights] = useState<typeof reports[0][]>([]);

  const handleChallengeSelect = (category) => {
    setSelectedChallenge(category);
    
    // Filter insights with highest rating (3) for selected category
    const filtered = reports.filter(report => 
      report.categoryRatings[category]?.value === 3
    );
    
    setRecommendedInsights(filtered);
    setStep(2);
  };

  const handleInsightSelect = (report) => {
    setSelectedInsights(current => {
      const exists = current.find(r => r.name === report.name);
      if (exists) {
        return current.filter(r => r.name !== report.name);
      }
      return [...current, report];
    });
  };

  const handleReset = () => {
    setStep(1);
    setSelectedChallenge('');
    setRecommendedInsights([]);
    setSelectedInsights([]);
  };

  return (
    <div className="space-y-8">
      {step === 1 && <IntroText />}
      
      {step === 1 && (
        <div>
          <h2 className="text-xl font-semibold mb-6">Select your CX challenge:</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleChallengeSelect(category)}
                  className={`p-6 text-left border rounded-lg transition hover:shadow-md ${
                    selectedChallenge === category 
                      ? 'border-blue-500 bg-blue-50 shadow-inner' 
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${selectedChallenge === category ? 'bg-blue-500' : 'bg-gray-300'}`} />
                    <span className="text-lg font-medium">{category}</span>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h1 className="text-2xl font-bold mb-6">Strongly Recommended Insights</h1>
          <p className="mb-6">
            These insights specifically focus on your selected challenge <span className="font-semibold">({selectedChallenge})</span>:
          </p>

          {recommendedInsights.length > 0 ? (
            recommendedInsights.map((report, index) => (
              <div 
                key={index} 
                className={`mb-6 border rounded-lg p-6 bg-white shadow cursor-pointer ${
                  selectedInsights.find(r => r.name === report.name) ? 'border-blue-500 ring-2 ring-blue-200' : ''
                }`}
                onClick={() => handleInsightSelect(report)}
              >
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id={`report-${index}`}
                    name={`report-${index}`}
                    checked={selectedInsights.some(r => r.name === report.name)}
                    onChange={() => handleInsightSelect(report)}
                    className="mt-1 mr-4 h-5 w-5"
                  />
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{index + 1}. {report.name.replace('Report', 'Insights')}</h2>
                    
                    <div className="mb-4">
                      <h3 className="font-semibold mb-1">Why These Insights:</h3>
                      <p className="text-gray-700">{report.criticalReason}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 bg-gray-100 rounded-lg">
              <p className="text-center text-gray-700">No strongly recommended insights available for this challenge. Please select a different challenge.</p>
            </div>
          )}

          <div className="mt-8 flex justify-center gap-4">
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setStep(1); }}
              className="px-6 py-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg font-semibold transition-colors"
            >
              Choose Different Challenge
            </a>
            <a 
              href="https://kronologic.me/1-click/zWxqVT2ASOytpMCtSReRDWxbBRVHiUbHjzqAm5x3Arg"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors shadow-sm"
            >
              Buy Insights
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default CXReportRecommender;